// StepProgress — 3-segment progress bar at top of the wizard.
//
// Each segment is one of:
//   - "current"   filled accent colour
//   - "complete"  checkmark + green
//   - "skipped"   dashed grey (task didn't require this step)
//   - "pending"   muted outline

import React from "react";
import { Check, MinusCircle } from "lucide-react";
import { stepRequired, stepCompleted, type WizardData, type WizardStep } from "./types.js";

interface Props {
  data: WizardData;
  current: WizardStep;
}

export default function StepProgress({ data, current }: Props): React.ReactElement {
  const segs: { step: WizardStep; label: string }[] = [
    { step: 1, label: "驗證" },
    { step: 2, label: "證據" },
    { step: 3, label: "簽名" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {segs.map(({ step, label }) => {
        const required = stepRequired(data.task, step);
        const completed = required && stepCompleted(data, step);
        const skipped = !required;
        const isCurrent = step === current && required && !completed;
        const stateCls =
          completed ? "bg-success/15 text-success border-success/40" :
          isCurrent ? "bg-accent/15 text-accent border-accent/60" :
          skipped   ? "bg-bg-raised/50 text-text-muted border-border-subtle" :
                      "bg-bg-raised text-text-secondary border-border-subtle";
        return (
          <div key={step} className={`rounded-xl border ${stateCls} px-2 py-2 text-center`}>
            <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider mb-0.5">
              {completed ? <Check className="h-3 w-3" /> :
               skipped   ? <MinusCircle className="h-3 w-3" /> :
                           <span className="font-mono">{step}</span>}
              <span>{label}</span>
            </div>
            <p className="text-[9px] text-text-muted">
              {completed ? "完成" : skipped ? "略過" : isCurrent ? "進行中" : "待辦"}
            </p>
          </div>
        );
      })}
    </div>
  );
}
